import { Button, EmptyState } from "@nexxt/ui";
import { Link } from "@tanstack/react-router";
import { errorText } from "../errors";
import { m } from "../paraglide/messages.js";
import { ErrorLine } from "../shell/error-line";
import { type FollowClient, followsChanges, useFollowing, useFollowToggle } from "./follow";

// What the user follows (ADR-281), in the bell: each object with its page, its status now and what of it reaches the
// bell (ADR-307), with the other level, where the object has one, and "Unfollow".

export function FollowingList({ client, onOpen }: { client: FollowClient; onOpen: () => void }) {
  const following = useFollowing(client);
  const toggle = useFollowToggle(client);
  const items = following.data?.items ?? [];
  return (
    <>
      {following.error && <ErrorLine className="px-4 py-3">{errorText(following.error)}</ErrorLine>}
      {following.isSuccess && items.length === 0 && (
        <EmptyState title={m.following_empty_title()}>{m.following_empty_hint()}</EmptyState>
      )}
      <ul className="divide-y divide-border">
        {items.map((item) => {
          const link = client.linkOf(item);
          const all = item.level === "all";
          const levels = followsChanges(item);
          const status = client.statusOf(item);
          const content = (
            <>
              <span className="block break-words font-medium">{item.reference}</span>
              <span className="block text-xs text-muted-foreground">
                {levels
                  ? m.following_status_level({
                      status,
                      level: all ? m.following_level_all() : m.following_level_status(),
                    })
                  : m.following_status({ status })}
              </span>
            </>
          );
          return (
            <li
              key={`${item.entityType}:${item.entityId}`}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 text-sm"
            >
              {link ? (
                <Link {...link} className="min-w-0 flex-1 rounded-sm py-1 hover:underline" onClick={onOpen}>
                  {content}
                </Link>
              ) : (
                <span className="min-w-0 flex-1 py-1">{content}</span>
              )}
              <span className="flex shrink-0 gap-1">
                {levels && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={
                      all
                        ? m.following_to_status({ reference: item.reference })
                        : m.following_to_all({ reference: item.reference })
                    }
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate({ target: item, level: all ? "status" : "all" })}
                  >
                    {all ? m.follow_status() : m.follow_all()}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={m.following_unfollow({ reference: item.reference })}
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ target: item, level: null })}
                >
                  {m.follow_stop()}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
